/**
 * @param {HTMLElement} element
 * @return {number}
 */
function offsetTop(element, acc = 0) {
	if (element.offsetParent) {
		return offsetTop(element.offsetParent, acc + element.offsetTop);
	}
	return acc + element.offsetTop;
}

/**
 * @class Parallax
 * @property {HTMLElement} element
 * @property {{y: number, variable: boolean}} options
 */
class Parallax {
	/**
	 * @param {HTMLElement} element
	 * @memberof Parallax
	 */
	constructor(element) {
		this.element = element;
		this.options = this.parseAttribute();
		this.onScroll = this.onScroll.bind(this);
		this.onIntersection = this.onIntersection.bind(this);
		this.onResize = this.onResize.bind(this);
		this.elementY = offsetTop(this.element) + this.element.offsetHeight / 2;
		const observer = new IntersectionObserver(this.onIntersection);
		observer.observe(element);
		this.onScroll();
	}

	parseAttribute() {
		const defaultOptions = {
			y: 0.4,
			variable: false,
		};
		if (this.element.dataset.parallax.startsWith('{')) {
			return {
				...defaultOptions,
				...JSON.parse(this.element.dataset.parallax),
			};
		}
		return {
			...defaultOptions,
			y: parseFloat(this.element.dataset.parallax),
		};
	}

	/**
	 * @param {IntersectionObserverEntry[]} entries
	 * @memberof Parallax
	 */
	onIntersection(entries) {
		for (const entry of entries) {
			if (entry.isIntersecting) {
				document.addEventListener('scroll', this.onScroll);
				window.addEventListener('resize', this.onResize);
				this.elementY =
					offsetTop(this.element) + this.element.offsetHeight / 2;
			} else {
				document.removeEventListener('scroll', this.onScroll);
				window.removeEventListener('resize', this.onResize);
			}
		}
	}

	onResize() {
		this.elementY = offsetTop(this.element) + this.element.offsetHeight / 2;
		this.onScroll();
	}

	onScroll() {
		window.requestAnimationFrame(() => {
			const screenY = window.scrollY + window.innerHeight / 2;
			const diffY = this.elementY - screenY;
			const translateY = diffY * -1 * this.options.y;
			if (this.options.variable) {
				this.element.style.setProperty(
					'--parallaxY',
					`${translateY}px`
				);
			} else {
				this.element.style.setProperty(
					'transform',
					`translateY(${translateY}px)`
				);
			}
		});
	}

	/**
	 * @static
	 * @memberof Parallax
	 * <@returns {Parallax[]}
	 */
	static bind() {
		Array.from(document.querySelectorAll('[data-parallax')).map(
			(element) => {
				return new Parallax(element);
			}
		);
	}
}

Parallax.bind();
